import { capitalize } from 'lodash';
import toNumber from 'lodash/toNumber';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Field } from '@atlaskit/form';
import { PricingPlanCycle } from '@atlassian/cssxp-ccp-types';
import {
  BillingStatus,
  EntitlementDetails,
  LinkedDestinationEntitlement,
  LinkedProductTypes,
  Maybe,
  MetadataType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { EntitlementEdition, NOT_AVAILABLE } from '@atlassian/cssxp-utils-entitlement';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { DynamicResultTable, NoDataContentInTable, SandboxLozenge, withApollo } from '@css-xp/base';
import { getProductName } from '@css-xp/base/entitlement-utils';
import { BillEstimateOrderItemDto } from '@css-xp/common-services/src/api/gateways/ccp-gateways/src/ccp-api-order';
import EntitlementIcon from '../../../../../../../base/entitlement-icon/entitlement-icon';
import { getBillPeriod } from '../../../utils/get-bill-period';
import { formatToUSD } from '../utils/currency-display';
import { Estimate } from './price-difference/price-difference';

export interface ProductFamilyTableProps {
  entitlement: EntitlementDetails;
  linkedEntitlementsData?: LinkedDestinationEntitlement[];
  productType?: Maybe<LinkedProductTypes>;
  parentContainerID?: string;
  billingCycle?: PricingPlanCycle;
  pricingPlan?: string;
  userTier?: string;
  estimatedBill: Estimate;
  anchorTime?: number;
  isLoading?: boolean;
}

const emptyStateOrderProp = {
  header: '',
  description: 'No other subscriptions',
  imageUrl: '',
  testId: 'no-results-subscriptions-apps',
};

export const ProductFamilyTable = ({
  entitlement,
  linkedEntitlementsData = [],
  productType,
  parentContainerID,
  billingCycle,
  pricingPlan,
  userTier,
  estimatedBill,
  anchorTime,
  isLoading = false,
}: ProductFamilyTableProps) => {
  const isEstimateLoading = !!estimatedBill?.loading || isLoading;
  const currentEntitlementEstimate = estimatedBill?.response?.estimate;
  const linkedEntitlementsBillEstimate = estimatedBill?.response?.linkedEstimates;

  const getDiscountAmount = (items: BillEstimateOrderItemDto['items']) => {
    if (items && items.length > 0) {
      const nonDstItems = items.filter((item) => item?.metadata?.type !== MetadataType.ADMIN_FEE);
      const flattenAdjustments = (
        flattenedList: BillEstimateOrderItemDto['items'][0]['adjustments'],
        item: BillEstimateOrderItemDto['items'][0]
      ) => {
        const allAdjustmentsForItem = item?.adjustments ?? [];
        flattenedList = flattenedList.concat(allAdjustmentsForItem);
        return flattenedList;
      };
      const allAdjustments = nonDstItems.reduce(flattenAdjustments, []);

      const discount = allAdjustments.reduce((sum, currentAdjustment) => {
        sum += currentAdjustment?.amount || 0;
        return sum;
      }, 0);
      return discount;
    }
    return null;
  };

  const finalLinkedEntitlements: Array<LinkedDestinationEntitlement> = useMemo(() => {
    let filterLinkedEntsWithoutContainerId;
    if (productType === LinkedProductTypes.JIRA && parentContainerID) {
      filterLinkedEntsWithoutContainerId = linkedEntitlementsData.filter(
        (linkedEnt) =>
          !(
            (linkedEnt.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
            linkedEnt.entitlementId === parentContainerID
          )
      );
    } else {
      filterLinkedEntsWithoutContainerId = linkedEntitlementsData.filter(
        (linkedEnt) => !(linkedEnt.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX)
      );
    }

    const currentEntitlementDetails: LinkedDestinationEntitlement = {
      billingGroupId: entitlement?.billingGroupId,
      billingStatus: entitlement?.billingStatus as BillingStatus,
      edition: pricingPlan,
      entitlementId: entitlement?.id,
      entitlementNumber: entitlement?.entitlementNumber,
      isApp: entitlement?.isApp,
      isSandbox: entitlement?.isSandbox,
      latestPeriodEndDate: entitlement?.latestPeriodEndDate,
      logoId: entitlement?.logoId,
      offeringId: entitlement?.offeringId,
      parentSen: entitlement?.parentSen,
      productId: entitlement?.productId,
      productName: entitlement?.productName,
      subscriptionId: entitlement?.subscriptionId,
      transactionAccountId: entitlement?.transactionAccountId,
      grandTotal: currentEntitlementEstimate?.grandTotal,
      discount: getDiscountAmount(currentEntitlementEstimate?.items ?? []),
      billPeriod: getBillPeriod(currentEntitlementEstimate?.items, false),
      userTier: toNumber(userTier),
    };

    filterLinkedEntsWithoutContainerId.splice(0, 0, currentEntitlementDetails);
    return filterLinkedEntsWithoutContainerId;
  }, [
    linkedEntitlementsData,
    parentContainerID,
    productType,
    entitlement,
    currentEntitlementEstimate,
    userTier,
    pricingPlan,
  ]);

  const finalLinkedEntitlementsWithEstimates: Array<LinkedDestinationEntitlement> = useMemo(() => {
    return finalLinkedEntitlements.map((linkedEnt: LinkedDestinationEntitlement) => {
      linkedEntitlementsBillEstimate?.map((ent) => {
        if (ent.entitlementId === linkedEnt.entitlementId) {
          linkedEnt.grandTotal = ent.grandTotal;
          linkedEnt.discount = getDiscountAmount(ent?.items ?? []);
          linkedEnt.billPeriod = getBillPeriod(ent.items, false);
        }
      });
      return linkedEnt;
    });
  }, [finalLinkedEntitlements, linkedEntitlementsBillEstimate]);

  const getRows = () => {
    return finalLinkedEntitlementsWithEstimates.map((item: LinkedDestinationEntitlement, index) => {
      const cellData = [
        {
          content: (
            <div className="subscription-details">
              <EntitlementIcon
                logoId={item?.logoId as string}
                productId={item?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <span className="product-name">{getProductName(item)}</span>
              {item?.isSandbox && <SandboxLozenge />}
              {item?.entitlementNumber ? (
                <div className="entitlement-number">{item?.entitlementNumber}</div>
              ) : (
                <div className="entitlement-number" aria-label="Not available" data-testid="not-available">
                  {NOT_AVAILABLE}
                </div>
              )}
            </div>
          ),
        },
        {
          content: (
            <div>
              <p>{capitalize(billingCycle)}</p>
              {item?.billPeriod && <p className="bill-period">{item.billPeriod}</p>}
            </div>
          ),
        },
        {
          content: <>{capitalize(item?.edition as string)}</>,
        },
        {
          content: <>{item?.userTier ?? <NoDataContentInTable />}</>,
        },
        {
          content: <>{formatToUSD(item?.discount ?? 0)}</>,
        },
        {
          content: <>{formatToUSD(item?.grandTotal ?? 0)}</>,
        },
      ];

      return {
        key: item.entitlementId ? item.entitlementId : index.toString(),
        cells: cellData,
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Subscription',
        shouldTruncate: true,
        width: 35,
      },
      {
        content: 'Billing cycle',
        width: 10,
      },
      {
        content: 'Pricing',
        width: 10,
      },
      {
        content: isStatuspage(entitlement?.productId) ? 'Units' : 'User tier',
        width: 10,
      },
      {
        content: 'Discount',
        width: 10,
      },
      {
        content: 'Price',
        width: 10,
      },
    ];

    return {
      cells,
    };
  };

  const renderBeforeTable = () => {
    const shouldRenderBillDate = !isStatuspage(entitlement?.productId) && anchorTime;
    if (shouldRenderBillDate) {
      return (
        <Field name="billDate" id="billDate" label="Bill date" defaultValue={anchorTime}>
          {() => <p>{moment.utc(anchorTime).format('MMM DD, YYYY')}</p>}
        </Field>
      );
    }
    return null;
  };

  return (
    <div className="product-family-table-container" data-testid="product-family-table-container">
      <DynamicResultTable
        testId="product-family-table"
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={isEstimateLoading}
        beforeTable={renderBeforeTable()}
      />
    </div>
  );
};

export default withApollo(ProductFamilyTable);
